// Author: Ahsan Saleem
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { auth } from './api.js';
import { SystemProvider } from './ui/system.js';
import { Boot } from './ui/Boot.js';
import { Shell } from './ui/Shell.js';
import { Login } from './screens/Login.js';
import { Simulation } from './screens/Simulation.js';

// The V2 experience is now the whole app — V1 is replaced. Every screen is its own
// lazy chunk. Legacy bare paths (/pulse, /stock, …) redirect to their V2 equivalent so
// existing bookmarks and links keep resolving. V1 screen components remain in the repo
// (unrouted) as a fallback; re-routing them is a one-line revert.
const PulseV2 = lazy(() => import('./v2/screens/PulseV2.js').then((m) => ({ default: m.PulseV2 })));
const FacilityCanvasV2 = lazy(() => import('./v2/screens/FacilityCanvasV2.js').then((m) => ({ default: m.FacilityCanvasV2 })));
const AlertsV2 = lazy(() => import('./v2/screens/AlertsV2.js').then((m) => ({ default: m.AlertsV2 })));
const WorkV2 = lazy(() => import('./v2/screens/WorkV2.js').then((m) => ({ default: m.WorkV2 })));
const SupplyV2 = lazy(() => import('./v2/screens/SupplyV2.js').then((m) => ({ default: m.SupplyV2 })));
const TradeV2 = lazy(() => import('./v2/screens/TradeV2.js').then((m) => ({ default: m.TradeV2 })));
const StockV2 = lazy(() => import('./v2/screens/StockV2.js').then((m) => ({ default: m.StockV2 })));
const ProcurementV2 = lazy(() => import('./v2/screens/ProcurementV2.js').then((m) => ({ default: m.ProcurementV2 })));
const PartiesV2 = lazy(() => import('./v2/screens/PartiesV2.js').then((m) => ({ default: m.PartiesV2 })));
const TaxV2 = lazy(() => import('./v2/screens/TaxV2.js').then((m) => ({ default: m.TaxV2 })));
const CropsV2 = lazy(() => import('./v2/screens/CropsV2.js').then((m) => ({ default: m.CropsV2 })));
const CatalogV2 = lazy(() => import('./v2/screens/CatalogV2.js').then((m) => ({ default: m.CatalogV2 })));
const ComplianceV2 = lazy(() => import('./v2/screens/ComplianceV2.js').then((m) => ({ default: m.ComplianceV2 })));
const ReceivingV2 = lazy(() => import('./v2/screens/ReceivingV2.js').then((m) => ({ default: m.ReceivingV2 })));
const ProductionV2 = lazy(() => import('./v2/screens/ProductionV2.js').then((m) => ({ default: m.ProductionV2 })));
const AccountingV2 = lazy(() => import('./v2/screens/AccountingV2.js').then((m) => ({ default: m.AccountingV2 })));
const ReportsV2 = lazy(() => import('./v2/screens/ReportsV2.js').then((m) => ({ default: m.ReportsV2 })));
const RulesV2 = lazy(() => import('./v2/screens/RulesV2.js').then((m) => ({ default: m.RulesV2 })));
const MaintenanceV2 = lazy(() => import('./v2/screens/MaintenanceV2.js').then((m) => ({ default: m.MaintenanceV2 })));
const EdgeV2 = lazy(() => import('./v2/screens/EdgeV2.js').then((m) => ({ default: m.EdgeV2 })));
const TraceV2 = lazy(() => import('./v2/screens/TraceV2.js').then((m) => ({ default: m.TraceV2 })));
const RecordV2 = lazy(() => import('./v2/screens/RecordV2.js').then((m) => ({ default: m.RecordV2 })));
const SettingsV2 = lazy(() => import('./v2/screens/SettingsV2.js').then((m) => ({ default: m.SettingsV2 })));
const OnboardV2 = lazy(() => import('./v2/screens/OnboardV2.js').then((m) => ({ default: m.OnboardV2 })));

// Legacy V1 path → V2 destination. Keeps old bookmarks/links alive after the merge.
const LEGACY: Record<string, string> = {
  '/pulse': '/v2', '/today': '/v2/work', '/facility': '/v2/canvas', '/alerts': '/v2/alerts',
  '/maintenance': '/v2/maintenance', '/compliance': '/v2/compliance', '/production': '/v2/production',
  '/reports': '/v2/reports', '/record': '/v2/record', '/edge': '/v2/edge', '/parties': '/v2/partners',
  '/catalog': '/v2/catalog', '/stock': '/v2/stock', '/procurement': '/v2/procurement',
  '/receiving': '/v2/receiving', '/billing': '/v2/supply', '/sales': '/v2/trade', '/tax': '/v2/tax',
  '/accounting': '/v2/accounting', '/trace': '/v2/trace', '/crops': '/v2/crops', '/rules': '/v2/rules',
  '/onboard': '/v2/onboard', '/settings': '/v2/settings',
};

function RequireAuth({ children }: { children: React.ReactNode }) {
  if (!auth.token || !auth.siteId) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

const S = (el: React.ReactNode) => <Suspense fallback={<div className="today" />}>{el}</Suspense>;

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={
        <RequireAuth>
          <SystemProvider>
            <Boot><Shell /></Boot>
          </SystemProvider>
        </RequireAuth>
      }>
        {/* The experience */}
        <Route path="/v2" element={S(<PulseV2 />)} />
        <Route path="/v2/pulse" element={S(<PulseV2 />)} />
        <Route path="/v2/canvas" element={S(<FacilityCanvasV2 />)} />
        <Route path="/v2/alerts" element={S(<AlertsV2 />)} />
        <Route path="/v2/work" element={S(<WorkV2 />)} />
        <Route path="/v2/supply" element={S(<SupplyV2 />)} />
        <Route path="/v2/trade" element={S(<TradeV2 />)} />
        <Route path="/v2/stock" element={S(<StockV2 />)} />
        <Route path="/v2/procurement" element={S(<ProcurementV2 />)} />
        <Route path="/v2/partners" element={S(<PartiesV2 />)} />
        <Route path="/v2/tax" element={S(<TaxV2 />)} />
        <Route path="/v2/crops" element={S(<CropsV2 />)} />
        <Route path="/v2/catalog" element={S(<CatalogV2 />)} />
        <Route path="/v2/compliance" element={S(<ComplianceV2 />)} />
        <Route path="/v2/receiving" element={S(<ReceivingV2 />)} />
        <Route path="/v2/production" element={S(<ProductionV2 />)} />
        <Route path="/v2/accounting" element={S(<AccountingV2 />)} />
        <Route path="/v2/reports" element={S(<ReportsV2 />)} />
        <Route path="/v2/rules" element={S(<RulesV2 />)} />
        <Route path="/v2/maintenance" element={S(<MaintenanceV2 />)} />
        <Route path="/v2/edge" element={S(<EdgeV2 />)} />
        <Route path="/v2/trace" element={S(<TraceV2 />)} />
        <Route path="/v2/record" element={S(<RecordV2 />)} />
        <Route path="/v2/settings" element={S(<SettingsV2 />)} />
        <Route path="/v2/onboard" element={S(<OnboardV2 />)} />

        {/* Legacy V1 paths → V2 (bookmarks/links stay alive) */}
        {Object.entries(LEGACY).map(([from, to]) => <Route key={from} path={from} element={<Navigate to={to} replace />} />)}
        <Route path="/facility/:code" element={<Navigate to="/v2/canvas" replace />} />
        {/* Dev/demo tool only — never routed in a production build. */}
        {import.meta.env.DEV && <Route path="/simulation" element={<Simulation />} />}

        <Route path="*" element={<Navigate to="/v2" replace />} />
      </Route>
      <Route path="*" element={<Navigate to={auth.token ? '/v2' : '/login'} replace />} />
    </Routes>
  );
}
